import * as turf from '@turf/turf'
import type { Feature } from 'geojson'
import mapboxgl from 'mapbox-gl'
import type { ContextType } from 'react'
import { createRoot, type Root } from 'react-dom/client'

import FeaturePopup from '@/Components/Map/FeaturePopup'
import { ThemeContext } from '@/theme/ThemeProvider'

import type { SmartSearchOperationalHit } from '../types'
import SearchLayerPopup from './SearchLayerPopup'

export type ThemeContextValue = NonNullable<ContextType<typeof ThemeContext>>

type Hote = {
  container: HTMLDivElement
  root: Root
  popup: mapboxgl.Popup | null
}

// Une seule bulle de recherche a la fois : sa racine React est reutilisee d'un
// resultat a l'autre. Demonter une racine pendant un rendu est interdit, rendre
// null ne l'est pas.
let hote: Hote | null = null

function obtenirHote(): Hote {
  if (!hote) {
    const container = document.createElement('div')
    hote = { container, root: createRoot(container), popup: null }
  }
  return hote
}

function lngLatFromFeature(feature: Feature): mapboxgl.LngLatLike {
  const geom = feature.geometry
  if (!geom) return [0, 0]
  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates as [number, number]
    return [lng, lat]
  }
  try {
    const c = turf.center(feature as turf.Feature)
    const coords = c.geometry.coordinates as [number, number]
    return [coords[0], coords[1]]
  } catch {
    return [0, 0]
  }
}

export function removeSmartSearchHitPopup(): void {
  if (!hote) return
  hote.popup?.remove()
  hote.popup = null
  hote.root.render(null)
}

export function showSmartSearchHitPopup(
  map: mapboxgl.Map,
  hit: SmartSearchOperationalHit,
  themeCtx?: ThemeContextValue,
  propertiesOrder?: string[] | null,
): void {
  removeSmartSearchHitPopup()

  const h = obtenirHote()

  h.popup = new mapboxgl.Popup({
    maxWidth: '350px',
    className: 'elda-feature-popup',
    closeButton: false,
    closeOnClick: false,
    offset: [0, -10],
  })
    .setLngLat(lngLatFromFeature(hit.feature))
    .setDOMContent(h.container)
    .addTo(map)

  const ouverte = h.popup
  ouverte.on('close', () => {
    if (hote?.popup === ouverte) hote.popup = null
  })

  // Un resultat « entite » rend exactement la meme bulle qu'un clic sur la
  // carte ; un resultat « couche » n'a pas d'attributs a montrer.
  const contenu =
    hit.kind === 'feature' ? (
      <FeaturePopup
        feature={hit.feature}
        layerName={hit.layerName}
        propertiesOrder={propertiesOrder}
        onClose={removeSmartSearchHitPopup}
      />
    ) : (
      <SearchLayerPopup
        titre={hit.label}
        sousTitre="Couche · vue centrée sur les données"
        geometryType={hit.feature.geometry?.type}
        onClose={removeSmartSearchHitPopup}
      />
    )

  // La racine est detachee de l'arbre applicatif : sans ce Provider, le theme
  // retomberait systematiquement en clair.
  h.root.render(
    themeCtx ? (
      <ThemeContext.Provider value={themeCtx}>{contenu}</ThemeContext.Provider>
    ) : (
      contenu
    ),
  )
}
